import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { API, formatApiError } from "@/lib/api";
import { rupee } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { User, Package, Download, LogOut, Loader2, ShoppingBag, MapPin, Plus, Star, Pencil, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { New: "bg-mustard/30 text-brown", Confirmed: "bg-sage/30 text-forest", Processing: "bg-sage/30 text-forest", Shipped: "bg-forest/15 text-forest", Delivered: "bg-earth/15 text-earth", Cancelled: "bg-terracotta/20 text-terracotta" };

const EMPTY_ADDR = { name: "", phone: "", flat: "", street: "", area: "", city: "", state: "", pincode: "", is_default: false };

function Addresses({ user }) {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null); // null = closed, {} = add/edit
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/me/addresses").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ ...EMPTY_ADDR, name: user.name || "", phone: user.phone || "" }); setEditId(null); };
  const openEdit = (a) => { setForm({ ...EMPTY_ADDR, ...a }); setEditId(a.id); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    for (const k of ["name", "phone", "flat", "city", "state", "pincode"]) {
      if (!String(form[k] || "").trim()) return toast.error("Please fill all required fields");
    }
    setSaving(true);
    try {
      const req = editId ? api.put(`/me/addresses/${editId}`, form) : api.post("/me/addresses", form);
      const { data } = await req;
      setList(data); setForm(null); setEditId(null);
      toast.success(editId ? "Address updated" : "Address added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save address"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { const { data } = await api.delete(`/me/addresses/${id}`); setList(data); toast("Address removed"); }
    catch { toast.error("Could not delete"); }
  };
  const makeDefault = async (id) => {
    try { const { data } = await api.post(`/me/addresses/${id}/default`); setList(data); toast.success("Default address updated"); }
    catch { toast.error("Could not update"); }
  };

  if (list === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  return (
    <div className="space-y-4" data-testid="addresses-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl text-forest">Saved Addresses</h2>
        {!form && <Button onClick={openAdd} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="add-address-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Address</Button>}
      </div>

      {form && (
        <div className="bg-white rounded-2xl soft-shadow p-6 space-y-4" data-testid="address-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Fld label="Name *"><Input value={form.name} onChange={set("name")} className="rounded-xl bg-sand" data-testid="addr-name" /></Fld>
            <Fld label="Mobile *"><Input value={form.phone} onChange={set("phone")} className="rounded-xl bg-sand" data-testid="addr-phone" /></Fld>
            <Fld label="Flat / House No *"><Input value={form.flat} onChange={set("flat")} className="rounded-xl bg-sand" data-testid="addr-flat" /></Fld>
            <Fld label="Street"><Input value={form.street} onChange={set("street")} className="rounded-xl bg-sand" data-testid="addr-street" /></Fld>
            <Fld label="Area / Locality"><Input value={form.area} onChange={set("area")} className="rounded-xl bg-sand" data-testid="addr-area" /></Fld>
            <Fld label="City *"><Input value={form.city} onChange={set("city")} className="rounded-xl bg-sand" data-testid="addr-city" /></Fld>
            <Fld label="State *"><Input value={form.state} onChange={set("state")} className="rounded-xl bg-sand" data-testid="addr-state" /></Fld>
            <Fld label="PIN Code *"><Input value={form.pincode} onChange={set("pincode")} className="rounded-xl bg-sand" data-testid="addr-pincode" /></Fld>
          </div>
          <label className="flex items-center gap-2 text-sm text-brown cursor-pointer">
            <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="addr-default-check" /> Set as default delivery address
          </label>
          <div className="flex gap-3">
            <Button onClick={save} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-address-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button>
            <Button onClick={() => { setForm(null); setEditId(null); }} variant="outline" className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}

      {list.length === 0 && !form ? (
        <div className="text-center py-16 bg-white rounded-2xl soft-shadow">
          <MapPin className="w-12 h-12 text-sage mx-auto mb-3" />
          <p className="text-brown/70">No saved addresses yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {list.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl soft-shadow p-5 relative" data-testid={`address-${a.id}`}>
              {a.is_default && <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-xs font-500 text-forest bg-sage/25 px-2.5 py-1 rounded-full"><Star className="w-3 h-3 fill-forest" /> Default</span>}
              <div className="font-600 text-ink">{a.name}</div>
              <div className="text-sm text-brown/70">{a.phone}</div>
              <div className="text-sm text-brown/80 mt-2 leading-relaxed">
                {[a.flat, a.street].filter(Boolean).join(", ")}<br />
                {[a.area, a.city].filter(Boolean).join(", ")}<br />
                {[a.state, a.pincode].filter(Boolean).join(" - ")}
              </div>
              <div className="flex items-center gap-3 mt-4 pt-3 border-t border-sage/15">
                {!a.is_default && <button onClick={() => makeDefault(a.id)} className="text-xs font-500 text-forest hover:text-terracotta" data-testid={`set-default-${a.id}`}>Set default</button>}
                <button onClick={() => openEdit(a)} className="text-xs font-500 text-forest hover:text-terracotta inline-flex items-center gap-1" data-testid={`edit-address-${a.id}`}><Pencil className="w-3 h-3" /> Edit</button>
                <button onClick={() => remove(a.id)} className="text-xs font-500 text-terracotta hover:opacity-70 inline-flex items-center gap-1 ml-auto" data-testid={`delete-address-${a.id}`}><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Profile({ user }) {
  const { setUser } = useApp();
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", email: user.email || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/profile", form);
      setUser({ ...user, name: data.name, phone: data.phone || "", email: data.email || "" });
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update profile"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-2xl soft-shadow p-6 max-w-md space-y-4" data-testid="profile-panel">
      <Fld label="Name"><Input value={form.name} onChange={set("name")} className="rounded-xl bg-sand" data-testid="profile-name" /></Fld>
      <Fld label="Mobile Number"><Input value={form.phone} onChange={set("phone")} className="rounded-xl bg-sand" data-testid="profile-phone" /></Fld>
      <Fld label="Email (optional)"><Input type="email" value={form.email} onChange={set("email")} className="rounded-xl bg-sand" data-testid="profile-email" placeholder="Add an email for order updates" /></Fld>
      <div className="flex justify-between border-b border-sage/15 pb-2"><span className="text-olive text-sm">Role</span><span className="text-ink font-500">{user.role}</span></div>
      <Button onClick={save} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-profile-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Profile</>}</Button>
    </div>
  );
}

function Fld({ label, children }) {
  return <div><Label className="text-sm text-brown mb-1.5 block">{label}</Label>{children}</div>;
}

export default function Account() {
  const { user, authReady, logout } = useApp();
  const nav = useNavigate();
  const [orders, setOrders] = useState(null);

  useEffect(() => { if (authReady && !user) nav("/login"); }, [authReady, user, nav]);
  useEffect(() => { if (user) api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, [user]);

  if (!user) return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl bg-forest flex items-center justify-center"><User className="w-7 h-7 text-mustard" /></span>
          <div><h1 className="font-heading text-3xl text-forest">Hi, {user.name}</h1><p className="text-sm text-brown/70">{user.phone || user.email}</p></div>
        </div>
        <Button onClick={() => { logout(); nav("/"); }} variant="outline" className="rounded-full border-terracotta text-terracotta hover:bg-terracotta hover:text-white" data-testid="logout-btn"><LogOut className="w-4 h-4 mr-1.5" /> Logout</Button>
      </div>

      <Tabs defaultValue="orders">
        <TabsList className="bg-sand rounded-full h-auto p-1 flex-wrap">
          <TabsTrigger value="orders" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream px-5 py-2"><Package className="w-4 h-4 mr-1.5" /> Orders</TabsTrigger>
          <TabsTrigger value="addresses" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream px-5 py-2" data-testid="tab-addresses"><MapPin className="w-4 h-4 mr-1.5" /> Addresses</TabsTrigger>
          <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream px-5 py-2"><User className="w-4 h-4 mr-1.5" /> Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          {!orders ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> :
            orders.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl soft-shadow">
                <ShoppingBag className="w-12 h-12 text-sage mx-auto mb-3" />
                <p className="text-brown/70">You have no orders yet.</p>
                <Link to="/shop"><Button className="mt-4 rounded-full bg-forest hover:bg-earth text-cream">Start Shopping</Button></Link>
              </div>
            ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-2xl soft-shadow p-5" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div><span className="font-600 text-forest">{o.order_number}</span><span className="text-xs text-muted-foreground ml-2">{o.created_at?.slice(0, 10)}</span></div>
                    <span className={`text-xs font-500 px-3 py-1 rounded-full ${STATUS_COLOR[o.status] || "bg-sand text-brown"}`}>{o.status}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {o.items.slice(0, 4).map((i, idx) => <img key={idx} src={i.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-sand" />)}
                    {o.items.length > 4 && <span className="text-sm text-muted-foreground">+{o.items.length - 4}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-sage/20">
                    <span className="font-600 text-forest">{rupee(o.total)}</span>
                    <a href={`${API}/orders/${o.id}/invoice`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-forest font-500 hover:text-terracotta" data-testid={`invoice-btn-${o.id}`}><Download className="w-4 h-4" /> Invoice</a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="addresses" className="mt-6"><Addresses user={user} /></TabsContent>
        <TabsContent value="profile" className="mt-6"><Profile user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}
